import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, Send } from "lucide-react";
import { useI18n } from "@/i18n";
import { PageHero } from "@/pages/PageHero";
import { WhatsAppLink } from "@/components/layout/WhatsAppButton";
import { img } from "@/lib/gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOPICS = ["product", "order", "payment", "technical", "reseller", "other"];
const EMPTY = { first_name: "", last_name: "", company: "", email: "", phone: "", country: "", subject: "product", message: "", accept_privacy: false, website: "" };

const Input = ({ name, label, value, set, type = "text", required = false, children }) => (
  <label className="block text-sm text-[var(--steel)]">
    <span>{label}{required && <span className="text-[var(--gold)]"> *</span>}</span>
    {children || <input type={type} value={value} onChange={(e) => set(name, e.target.value)} required={required} data-testid={`contact-${name}`} className="mt-2 w-full border border-white/15 bg-[#0B1530] px-4 py-3 text-white outline-none focus:border-[var(--gold)]" />}
  </label>
);

export default function Contact() {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const copy = t.contact;

  useEffect(() => {
    const subject = params.get("subject");
    if (TOPICS.includes(subject)) setForm((current) => ({ ...current, subject }));
  }, [params]);

  const set = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const submit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    try {
      const response = await axios.post(`${API}/contacts`, { ...form, lang });
      setResult(response.data);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="MB300 · by NELVIS FRANCE" title={copy.title} text={copy.intro} image={img(13)} />
      <section className="container-x grid gap-14 pb-28 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <h2 className="font-display text-3xl font-bold uppercase">{copy.directTitle}</h2>
          <p className="mt-4 leading-relaxed text-[var(--steel)]">{copy.directText}</p>
          <WhatsAppLink testId="contact-whatsapp-button" className="mt-7 bg-[#25D366] px-5 py-3 font-bold text-[#07150C]">{t.whatsapp.label}</WhatsAppLink>
          <p className="mt-8 text-sm text-[var(--steel-dim)]" data-testid="contact-email-address">contact@nelvis-france.com</p>
        </div>
        <div className="lg:col-span-8">
          {status === "success" ? (
            <div className="border border-[var(--gold)] p-8" data-testid="contact-success">
              <Check className="h-9 w-9 text-[var(--gold)]" />
              <h2 className="font-display mt-5 text-4xl font-bold uppercase">{copy.successTitle}</h2>
              <p className="mt-4 text-[var(--steel)]">{copy.successText}</p>
              <p className="mt-3 font-mono text-[var(--gold)]" data-testid="contact-reference">{result.reference}</p>
              <p className="mt-4 text-sm text-white" data-testid="contact-email-status">{result.confirmation_email_sent ? copy.emailSent : copy.emailPending}</p>
              <Link to="/" data-testid="contact-back-home" className="mt-8 inline-flex border-b border-[var(--gold)] pb-1 text-sm uppercase">{t.common.backHome}</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="contact-form">
              <Input name="first_name" label={copy.fields.first_name} value={form.first_name} set={set} required />
              <Input name="last_name" label={copy.fields.last_name} value={form.last_name} set={set} required />
              <Input name="company" label={copy.fields.company} value={form.company} set={set} />
              <Input name="email" type="email" label={copy.fields.email} value={form.email} set={set} required />
              <Input name="phone" label={copy.fields.phone} value={form.phone} set={set} />
              <Input name="country" label={copy.fields.country} value={form.country} set={set} />
              <Input name="subject" label={copy.fields.subject} value={form.subject} set={set} required>
                <select value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject" className="mt-2 w-full border border-white/15 bg-[#0B1530] px-4 py-3 text-white outline-none focus:border-[var(--gold)]">
                  {TOPICS.map((topic) => <option key={topic} value={topic}>{copy.subjects[topic]}</option>)}
                </select>
              </Input>
              <label className="hidden" aria-hidden="true">Website<input value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex="-1" autoComplete="off" data-testid="contact-website" /></label>
              <label className="block text-sm text-[var(--steel)] sm:col-span-2">
                <span>{copy.fields.message} <span className="text-[var(--gold)]">*</span></span>
                <textarea value={form.message} onChange={(e) => set("message", e.target.value)} minLength="10" maxLength="3000" required rows="7" data-testid="contact-message" className="mt-2 w-full resize-y border border-white/15 bg-[#0B1530] px-4 py-3 text-white outline-none focus:border-[var(--gold)]" />
              </label>
              <label className="flex items-start gap-3 text-sm text-[var(--steel)] sm:col-span-2">
                <input type="checkbox" checked={form.accept_privacy} onChange={(e) => set("accept_privacy", e.target.checked)} required data-testid="contact-privacy" className="mt-1 h-4 w-4 accent-[#C9A55C]" />
                <span>{copy.privacy} <Link to="/confidentialite" target="_blank" data-testid="contact-privacy-link" className="underline">{copy.privacyLink}</Link>.</span>
              </label>
              {status === "error" && <p className="text-sm text-[#F59B9B] sm:col-span-2" data-testid="contact-error">{copy.error}</p>}
              <button type="submit" disabled={status === "sending"} data-testid="contact-submit" className="flex items-center justify-center gap-2 bg-[var(--gold)] px-6 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[var(--night)] disabled:opacity-60 sm:col-span-2">
                {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{status === "sending" ? copy.sending : copy.submit}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}